import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Accordion, Box } from "@chakra-ui/react";
import { SendView } from "@components/ActivityTypes/Send";
import { useSdkUtils, useMeeting } from "@hooks";
import { handleToolLinkClick } from "@utils/helpers";
import {
  useRespondToActivityMutation,
  useViewActivityMutation,
} from "@store/api/activities";

const ActivityToolAccordion = () => {
  const [respondToActivity] = useRespondToActivityMutation();
  const [viewActivity] = useViewActivityMutation();
  const { openUrl } = useSdkUtils();
  const { normalizedMeeting } = useMeeting();
  const liveTools = useSelector((state) => state.activity.liveTools);
  const profile = useSelector((state) => state.profile.data);

  const displayableActivityTools = useMemo(
    () =>
      liveTools.filter((liveTool) => {
        if (!liveTool?.settings?.blast) {
          return !!liveTool?.settings?.initialParticipant.includes(profile._id);
        }
        return true;
      }),
    [liveTools, profile._id]
  );

  const handleView = useCallback(
    async (activity) => {
      // Check if user has already viewed this activity
      const existingViewResponse = activity?.response?.find(
        (res) => res.user.userId === profile._id && res.viewedAt
      );

      if (existingViewResponse) {
        return; // Already viewed
      }

      try {
        await viewActivity({ id: activity._id }).unwrap();
      } catch (error) {
        console.error("Error tracking activity view:", error);
        throw error;
      }
    },
    [viewActivity, profile._id]
  );

  const handleClick = useCallback(
    async (activity) => {
      await handleToolLinkClick(activity.link, openUrl);

      // Check if user has already opened the link (not just viewed)
      const existingResponse = activity?.response?.find(
        (res) => res.user.userId === profile._id
      );

      // If user has already opened the link (has openedAt), don't respond again
      if (existingResponse && existingResponse.openedAt) {
        return;
      }

      try {
        await respondToActivity({
          id: activity._id,
          body: { response: [] },
        }).unwrap();
      } catch (error) {
        console.error("Error recording link click:", error);
      }
    },
    [respondToActivity, profile._id, openUrl]
  );

  if (profile?._id === normalizedMeeting?.hostId) {
    return null;
  }

  return (
    <Box>
      <Accordion allowToggle={true} defaultIndex={0}>
        {[...displayableActivityTools].reverse().map((activity) => (
          <SendView
            key={activity._id}
            activity={activity}
            handleClick={handleClick}
            handleView={handleView}
          />
        ))}
      </Accordion>
    </Box>
  );
};

export default ActivityToolAccordion;
